import type { SubmitEventHandler } from 'react';

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { z } from 'zod';

import { setFormSubmitErrors } from '../../form-hooks/lib/helpers';
import { getTestProps } from '../../lib/helpers';
import { emailSchema } from '../../lib/zodHelpers';

import Button from '../../components/Button/Button';
import Loader from '../../components/Loader/Loader';

import { WarningIcon } from '../../images/shapes';

import { useAppForm } from '../../form-hooks/form';

const initialValues = {
  email: '',
};

interface ChangePasswordRequestFormProps {
  /**
   * ChangePasswordRequestForm on submit handler
   */
  onSubmit: (
    email: string,
  ) => Promise<undefined | Record<string, string | string[]>>;
  /**
   * Form test id
   */
  testId?: string;
}

const ChangePasswordRequestForm = ({
  onSubmit,
  testId = '',
}: ChangePasswordRequestFormProps) => {
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [generalError, setGeneralError] = useState<string | string[]>();

  const validationSchema = useMemo(
    () =>
      z.object({
        email: emailSchema(t),
      }),
    [t],
  );

  const form = useAppForm({
    defaultValues: initialValues,
    onSubmit: async ({ value, formApi }) => {
      if (!onSubmit) return null;
      setIsLoading(true);
      const errors = await onSubmit(value.email);

      if (errors && Object.keys(errors).length > 0) {
        if (errors.general || errors.form) {
          setGeneralError(errors.general || errors.form);
        }
        setFormSubmitErrors(formApi, errors);
      } else {
        setIsSending(true);
      }

      setIsLoading(false);
    },
    validators: {
      onSubmit: validationSchema,
    },
  });

  const handleSubmit = useCallback<SubmitEventHandler>(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      form.handleSubmit();
    },
    [form],
  );

  return (
    <form.AppForm>
      {!isSending && (
        <form
          id={'change-password-request-from'}
          className="w-full max-w-xs m-auto"
          onSubmit={handleSubmit}
          noValidate={true}
          {...getTestProps(testId)}
        >
          <div
            className="flex flex-wrap mb-8 w-full"
            {...getTestProps(testId, 'email-container')}
          >
            <form.AppField name="email">
              {(field) => <field.Input placeholder={t('Global.Email')} />}
            </form.AppField>
          </div>

          <div className="flex flex-wrap mb-5">
            <div className="w-full flex flex-col justify-center text-center">
              <Button
                type="submit"
                buttonSize="lg"
                additionalClasses="w-full min-w-[300px] justify-center"
                additionalChildrenClasses="flex flex-row items-center"
                disabled={isLoading}
              >
                {t('Global.SendInstructions')}
                {isLoading && (
                  <Loader
                    size="tiny"
                    type="spinner-white"
                    additionalClasses="h-5 ml-2"
                  />
                )}
              </Button>
              {generalError && (
                <div className="text-red py-2 inline-flex">
                  <WarningIcon className="h-5 w-5 mr-2" />
                  <div className="block text-center">{generalError}</div>
                </div>
              )}
            </div>
          </div>
        </form>
      )}

      {isSending && (
        <div
          className={'text-center dark:text-slate-200'}
          {...getTestProps(testId, 'send')}
        >
          <span>{t('Form.FormChangePasswordRequestPasswordSendToEmail')}</span>
        </div>
      )}

      <div className="text-sm md:text-base leading-6 mt-4">
        <div className="w-full m-auto text-center dark:text-slate-200">
          {t('Global.GoBackTo')}
          <Link className="text-blue ml-1" to={`/${i18n.language}/login`}>
            {t('Form.FormLoginPage')}
          </Link>
        </div>
      </div>
    </form.AppForm>
  );
};

export default ChangePasswordRequestForm;
